import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
// The row shape NTF-07 publishes, imported rather than restated.
import type { TemplateSummary } from "@tdelivr/api-client/generated/notifications/template.js";

import { exportSheet, type ExportFormat } from "../export.ts";
import {
  BOARD_NOTE,
  fetchedNote,
  languagesCell,
  LISTING_NOTE,
  MACROS_SCOPE,
  macroDetail,
  macroPage,
  macrosOf,
  PER_FETCH,
  stateOf,
  statesPresent,
  type Macro,
  type MacroOrder,
} from "../macros.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { GridPager, GridToolbar, NoMatch } from "./DataGrid.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Saved replies an agent can send, and which languages each has.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `macros`, on that file's one `gridUI()`.
 *
 * **This page's recorded blocker was wrong on three of its four counts, and the fourth was the
 * valuable one.** It said nothing on the platform models a reply macro — "no name, no topic, no
 * body in any language" — and that SUP-04, which the design cites, is the customer-360 read model
 * and holds no saved reply. The sentence about SUP-04 is exactly right. But a saved reply with
 * variables, in a locale, with a publication status, is what **NTF-07** holds, and ADR 0043
 * published a summary listing of it weeks ago. `../macros.ts` carries the measurement.
 *
 * That is R-17's rule applied to a citation rather than a rejection: a rejection is a statement
 * about the question that was asked, and the question here was "does SUP-04 hold a macro".
 *
 * **Two of three columns have a source and the third is deliberately not derived.** `Macro` draws
 * the `templateKey`, which is a key and not a name — the finance console's reports board has the
 * same cell for the same reason. `Languages` is the locales NTF-07 holds under that key. `Topic` is
 * empty, and the key is **not parsed** into one: the design's `payments`, `refunds`, `trips` and
 * `privacy` look like a key's first segment, and reading it that way would invent a taxonomy NTF-07
 * does not have.
 *
 * **The board shows every message template the tenant has, and says so under the grid.** NTF-07
 * marks nothing as a support reply: there is no category, and `channel` is `sms | email | push |
 * in_app`, which is how a message is delivered rather than what it is for. The merchant portal's
 * products board carries the same shape of honesty for the same reason.
 *
 * **One row per macro, where the listing answers one per language.** That gathering is the only
 * arithmetic on this screen and it computes no figure: the locales are collected and the state is
 * taken from the variants. `published` wins, because a macro an agent can send is one with at least
 * one published language.
 *
 * **No tiles, where the design draws four.** `Macros · 34` and `Retired · 2` are counts over a
 * bounded listing (R-04). `Used today · 188` needs a record of a macro being **used**, which
 * nothing keeps. `Missing Swahili · 4` needs somebody to declare which languages a macro should
 * have, which nothing does — and it is the same absence as the design's `caution · sw missing` row
 * state.
 */

const templateApi = services.notifications__template;

export interface RepliesAndMacrosProps {
  readonly transport: Transport;
  /** Innermost last: tenant, console, group, page. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly templates: readonly TemplateSummary[];
  readonly cursor: string | undefined;
}

export function RepliesAndMacros({ transport, crumbs }: RepliesAndMacrosProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [state, setState] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<MacroOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);

  const load = useCallback(async () => {
    const client = templateApi.createTemplateClient(transport);
    try {
      const answered = await client.listTenantTemplateSummaries(String(PER_FETCH), undefined);
      setLoaded({ templates: answered.templates, cursor: answered.nextAfter ?? undefined });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, [MACROS_SCOPE]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The next page, from the cursor the service answered.
   *
   * **The cursor pages over variants and the board draws macros**, so one request can add languages
   * to a macro already on screen rather than adding a row. That is the honest consequence of
   * gathering, and it is why the note under the grid counts macros and says they were gathered from
   * the newest version of each language.
   */
  const loadMore = useCallback(async () => {
    const held = loaded;
    if (held?.cursor === undefined || fetching) return;
    setFetching(true);
    const client = templateApi.createTemplateClient(transport);
    try {
      const answered = await client.listTenantTemplateSummaries(String(PER_FETCH), held.cursor);
      setLoaded({
        templates: [...held.templates, ...answered.templates],
        cursor: answered.nextAfter ?? undefined,
      });
      setPage((current) => current + 1);
    } catch (error) {
      setFailure(stateForError(error, [MACROS_SCOPE]));
    } finally {
      setFetching(false);
    }
  }, [loaded, fetching, transport]);

  const macros = useMemo(() => macrosOf(loaded?.templates ?? []), [loaded]);
  const selected = macros.find((macro) => macro.templateKey === selectedKey);

  const view = useMemo(
    () => macroPage(macros, { search, order, page, perPage }, state),
    [macros, search, order, page, perPage, state],
  );

  const viewState = failure ?? stateForList(loaded === undefined ? undefined : macros);

  const clearFilters = (): void => {
    setSearch("");
    setState(undefined);
    setPage(1);
  };

  const onNext = (): void => {
    if (page < view.pageCount) setPage(page + 1);
    else void loadMore();
  };

  const note = fetchedNote(
    loaded === undefined ? undefined : macros.length,
    loaded?.cursor !== undefined,
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Macro</span>
      <span>Topic</span>
      <span>Languages</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      {/*
        The page and its detail panel in one positioned container, which is what docks the panel.

        Both docking rules are written against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes the panel a 420px second column, and below 1240 it is
        `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s own
        `position: relative`. This board had neither — the panel was rendered inside the page, and
        on seven of these twelve boards inside the closing-note callout, where it was a flex item
        beside an `info` glyph and a sentence.

        `Settlement.tsx` and `Invoices.tsx` were already this shape, which is why they are the
        shape copied here rather than a new one. Found by the census in
        `tooling/scripts/lib/detail-panels.mjs`: this panel had never been photographed, so
        nothing compared it against the design it is built from. Which docking is right at all is
        Q-297.
      */}
      <section className="page records macros">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Replies and macros"
          /*
           * NTF-07, not the design's `SUP-04`. That service is the customer-360 read model and holds
           * no saved reply, which the page's own recorded reason said correctly;
           * `tests/service-codes.test.ts` holds every code named on this console to being a service
           * the page reads from, so the correction could not be left in prose.
           */
          source="NTF-07"
          description="Saved replies agents can send, with variables filled from the case."
          action={
            <button
              type="button"
              className="records__primary"
              aria-disabled="true"
              title={
                "New macro is NTF-07's create command, which is on /internal/v1 and which this console holds no scope for. " +
                "A macro's body is withheld from this board as well: ADR 0043 publishes a template's identity and state and not its content."
              }
            >
              New macro
            </button>
          }
        />

        <div className="records__panel">
          <GridToolbar
            idPrefix="macros"
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setPage(1);
            }}
            state={state}
            onState={(next) => {
              setState(next);
              setPage(1);
            }}
            states={statesPresent(macros)}
            order={order}
            onOrder={setOrder}
            onExport={(format: ExportFormat) => {
              exportSheet(sheetFor(view.all), format, "replies-and-macros");
            }}
          />

          <StateView
            state={viewState}
            purpose="A saved reply appears here as soon as one is created for this tenant."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "records__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="records__rows">
              {view.rows.map((macro) => {
                const shown = stateOf(macro);
                return (
                  <div
                    key={macro.templateKey}
                    data-selected={macro.templateKey === selectedKey ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={macro.templateKey === selectedKey}
                    aria-label={`Open macro ${macro.templateKey}`}
                    onClick={() => setSelectedKey(macro.templateKey)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedKey(macro.templateKey);
                    }}
                    className="records__row"
                  >
                    {/*
                     * The design draws `Payment still checking`. NTF-07 publishes a key, so this is
                     * the key in mono — never a key prettified into a sentence, which is the refusal
                     * the finance console's reports board made for a trigger key.
                     */}
                    <span className="records__lead records__mono" title={macro.templateKey}>
                      {macro.templateKey}
                    </span>
                    <span
                      className="records__cell"
                      title={
                        "NTF-07 holds no topic on a template, and the key is deliberately not parsed into one: a macro whose key has no dot would get no topic, and one whose first segment is a service name would get a wrong one."
                      }
                    >
                      <span className="records__absent" aria-label="No topic is published">
                        —
                      </span>
                    </span>
                    <span
                      className="records__cell"
                      title={[
                        `delivered on ${macro.channels.join(", ")}`,
                        "The design flags a macro whose Swahili is missing. Nothing declares which languages a macro should have, so a macro with one language is indistinguishable from one that only ever needed one.",
                      ].join("\n")}
                    >
                      {languagesCell(macro)}
                    </span>
                    <span className="records__state">
                      <StateBadge
                        code="NTF-07"
                        status={macro.status}
                        classKey={shown.key}
                        label={shown.word}
                        title={`NTF-07 publishes ${macro.status}. ${shown.title}`}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? <NoMatch onClear={clearFilters} /> : null}
            <GridPager
              idPrefix="macros"
              page={page}
              pageCount={view.pageCount}
              perPage={perPage}
              onPerPage={(next) => {
                setPerPage(next);
                setPage(1);
              }}
              total={view.total}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={onNext}
              more={loaded?.cursor !== undefined}
              fetching={fetching}
            />
          </StateView>

          {note === undefined ? null : (
            <p className="records__bounded" role="status">
              {note}
            </p>
          )}

          <p className="records__bounded">{LISTING_NOTE}</p>
        </div>

        {/* The design's closing note, word for word, in its own `info` kind. */}
        <div className="records__note records__note--info">
          <span aria-hidden="true" className="records__note-glyph">
            info
          </span>
          <span>{BOARD_NOTE}</span>
        </div>
      </section>

      {selected === undefined ? null : (
        <RecordDetail
          record={macroDetail(selected)}
          open
          onClose={() => setSelectedKey(undefined)}
        />
      )}
    </div>
  );
}

/**
 * The rectangle an export writes: the columns on screen, plus the two a file is read without.
 *
 * `Delivered on` is the channel, which is on screen only as a hover and is the field a reader would
 * otherwise mistake the empty `Topic` column for. `Newest version` is what a macro changed to, and
 * a row read out of a file months later has no board to compare it against. The body is in neither:
 * the operation does not publish it.
 */
function sheetFor(rows: readonly Macro[]): {
  header: readonly string[];
  rows: readonly (readonly string[])[];
} {
  return {
    header: ["Macro", "Topic", "Languages", "State", "Delivered on", "Newest version"],
    rows: rows.map((macro) => [
      macro.templateKey,
      "",
      languagesCell(macro),
      stateOf(macro).word,
      macro.channels.join(", "),
      `v${String(macro.version)}`,
    ]),
  };
}
